import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import DotsLoader from "../../../components/icons/DotsLoader";
import EmptyState from "../../../components/shared/EmptyState";
import ActorCard, { Actor } from "./ActorCard";
import styles from "./ActorsList.module.scss";

interface ActorsListProps {
  data: Actor[];
  error: any;
  isFetching?: boolean;
  isLastData?: boolean;
  favoritesList?: number[];
  favouritesHandler?: (e: any, actor: Actor, isFav: boolean) => void;
  isFavoritePage?: boolean;
  setPageHandler: () => void;
}

const ActorsList = ({
  data,
  error,
  isFetching,
  isLastData,
  favoritesList,
  favouritesHandler,
  isFavoritePage = false,
  setPageHandler,
}: ActorsListProps) => {
  const paginationAnchorRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (isFetching || isLastData) return;

    const elem = paginationAnchorRef.current;
    const handleIntersection = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && data.length && !error) {
          setPageHandler();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.1,
    });

    if (elem) {
      observer.observe(elem);
    }

    return () => {
      if (elem) {
        observer.unobserve(elem);
      }
    };
  }, [setPageHandler, isFetching, isLastData, data?.length, error]);

  if (isFetching && !data.length) {
    return (
      <div className={styles.loaderWrapper}>
        <DotsLoader />
      </div>
    );
  }

  if (!data.length) {
    return (
      <EmptyState
        className={styles.emptyWrapper}
        variant={isFavoritePage ? "favorites" : "search"}
        text={t(isFavoritePage ? "emptyFavourites" : "emptySearch")}
      />
    );
  }

  return (
    <div className={styles.contentWrapper}>
      <h2 className={styles.title}>{t("actors")}</h2>
      <div className={styles.cardListWrapper}>
        {data.map((actor) => (
          <ActorCard
            key={actor.id}
            actor={actor}
            isFav={Boolean(favoritesList?.includes(actor.id))}
            favouritesHandler={favouritesHandler}
          />
        ))}
      </div>
      {!isLastData && (
        <div className={styles.loaderWrapper} ref={paginationAnchorRef}>
          <DotsLoader />
        </div>
      )}
    </div>
  );
};

export default ActorsList;
